import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// PO 2026-02 · Trial Paywall V1 — swallow the dev-server overlay for
// the flood of 402s that happen once as the paywall guard takes over
// the surface. Runs once at module load. Production has no CRA
// overlay so this is purely dev polish; behaviour of caller code is
// unchanged (the axios promise still rejects normally).
if (typeof window !== "undefined") {
  const _is_paywall_error = (e) => {
    if (!e) return false;
    const msg = String(e?.message || e?.reason?.message || e || "");
    return e.__jc_paywall_handled === true
        || e.reason?.__jc_paywall_handled === true
        || e?.response?.status === 402
        || e.reason?.response?.status === 402
        || /status code 402/i.test(msg);
  };
  window.addEventListener("unhandledrejection", (event) => {
    if (_is_paywall_error(event.reason)) event.preventDefault();
  });
  window.addEventListener("error", (event) => {
    if (_is_paywall_error(event.error)) event.preventDefault();
  });
}

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("jc_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // token invalid — clear and bounce to login (only outside login itself)
      if (!window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/signup")) {
        localStorage.removeItem("jc_token");
        localStorage.removeItem("jc_user");
        window.location.href = "/login";
      }
    }
    // PO 2026-02 · Trial Paywall V1 — surface 402 as a broadcast event
    // so ``AuthContext`` can flip the ``entitlement`` state and the
    // ``<PaywallRoute>`` guard shows the Trial Expired screen. Never
    // logs the contractor out — the reactivation flow lives at
    // ``/app/account`` and must remain reachable.
    if (err?.response?.status === 402) {
      try {
        window.dispatchEvent(new CustomEvent("jc:paywall_expired", {
          detail: err.response.data || {},
        }));
      } catch { /* ignore */ }
      // Tag the error so a global handler can suppress the dev-server
      // overlay for the expected 402 flood while <PaywallRoute> is
      // taking over the surface. Production has no CRA overlay so
      // this is purely dev polish.
      err.__jc_paywall_handled = true;
    }
    // Pass 4C · a removed member's next request returns 403 with
    // ``membership_inactive``. Clear the local session and bounce to a
    // dedicated landing page so no protected company data lingers. We
    // do NOT auto-create a new company on their behalf; the person can
    // sign in again to accept a fresh invitation into another company.
    if (
      err?.response?.status === 403 &&
      err?.response?.data?.detail === "membership_inactive" &&
      !window.location.pathname.startsWith("/no-access") &&
      !window.location.pathname.startsWith("/login")
    ) {
      localStorage.removeItem("jc_token");
      localStorage.removeItem("jc_user");
      window.location.href = "/no-access";
    }
    return Promise.reject(err);
  }
);

export const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

export const formatDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    const locale = lang === "es" ? "es-ES" : "en-US";
    return new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

// iter46 · Shared save-error diagnostic. Replaces generic "Save failed" toasts
// with an actionable message that includes the HTTP status and backend detail
// when available. Contract:
//   * 401 → session-expired message (axios interceptor will also bounce to /login)
//   * 403 → permission message
//   * 413 → payload too large
//   * 422 → validation detail from backend
//   * 5xx → transient-server hint, tells contractor their local changes are safe
//   * Network / no-response → connectivity hint, tells contractor their local changes are safe
//   * Fallback → include HTTP status so a screenshot is diagnostic
// The `context` string is the noun that appears in the fallback message
// ("your company profile", "your pricing", etc.).
export const explainSaveError = (err, context = "changes") => {
  if (err?.response) {
    const status = err.response.status;
    const detail =
      err.response.data?.detail ||
      (typeof err.response.data === "string" && err.response.data.length < 200
        ? err.response.data
        : null);
    if (status === 401) return "Your session expired — please sign in again.";
    if (status === 403) return "You don't have permission to save this — contact support.";
    if (status === 413) return "Attachment too large — please reduce and retry.";
    if (status === 422) return `We couldn't save — ${detail || "please check the fields and try again."}`;
    if (status >= 500) return `Server is briefly unavailable (HTTP ${status}). Your changes are still here — please retry in a few seconds.`;
    return `We couldn't save (HTTP ${status}). ${detail || "Please retry."}`;
  }
  if (err?.message === "Network Error" || err?.code === "ERR_NETWORK") {
    return "Network hiccup — check your connection. Your changes are still here.";
  }
  return `We couldn't save your ${context} — please retry in a moment.`;
};

// Download a PDF from any authenticated /api endpoint. Reads the
// Content-Disposition filename when present so the browser saves the doc with
// the safe slugified name the backend produced. Pure read — does not mutate.
export const downloadPdf = async (path, fallbackName = "document.pdf") => {
  const resp = await api.get(path, { responseType: "blob" });
  const disposition = resp.headers["content-disposition"] || "";
  const m = disposition.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : fallbackName;
  const url = window.URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return filename;
};
